import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { SkillCardSkeleton } from "@/components/Skeletons";
import { Seo } from "@/components/Seo";

const staticSkillsMap = {
  "React": { icon: "⚛", exp: "30+ Projects", focus: "React 19, Hooks & rendering optimization", details: "Expertise in concurrent rendering, custom hooks, state optimization, and layout reflow containment." },
  "React 19": { icon: "⚛", exp: "30+ Projects", focus: "React 19, Hooks & rendering optimization", details: "Expertise in concurrent rendering, custom hooks, state optimization, and layout reflow containment." },
  "Next.js": { icon: "▲", exp: "12 Apps", focus: "App Router & SSR/ISR pipelines", details: "Building highly-optimized serverside-rendered portals, path generation, dynamic SEO metadata, and routing hooks." },
  "TypeScript": { icon: "TS", exp: "4 Years", focus: "Strong typing & safety", details: "Strict type safety, custom utility types, and API response integration with Zod schemas." },
  "JavaScript (ES2023)": { icon: "JS", exp: "5+ Years", focus: "ES2023+ specifications", details: "Deep understanding of event loops, asynchronous programming, closures, and performance bottlenecks." },
  "JavaScript": { icon: "JS", exp: "5+ Years", focus: "ES2023+ specifications", details: "Deep understanding of event loops, asynchronous programming, closures, and performance bottlenecks." },
  "HTML5": { icon: "HTML", exp: "5+ Years", focus: "Semantic markup & accessibility", details: "Building fully accessible structures adhering strictly to semantic HTML5 standards and WCAG guidelines." },
  "CSS3": { icon: "CSS", exp: "5+ Years", focus: "Responsive grid layouts", details: "Expertise in CSS Grid, custom properties, animations, transitions, and modern flexbox layouts." },
  "Accessibility (a11y)": { icon: "♿", exp: "WCAG AA", focus: "Inclusive design layouts", details: "Handling screen reader navigation, tab focus management, keyboard control accessibility, and ARIA elements." },
  "Responsive Design": { icon: "📱", exp: "Universal", focus: "Fluid layout grids", details: "Designing pages that scale seamlessly from 320px mobile screens to large desktop monitors." },
  "Tailwind CSS": { icon: "🎨", exp: "Design Systems", focus: "Utility-first styling", details: "Integrating custom theme tokens, responsive utility hooks, and atomic layout variables." },
  "Shadcn UI": { icon: "🧩", exp: "Components", focus: "Tailwind primitives", details: "Building customized component sheets, dialogue drawers, and custom layout theme integrations." },
  "Radix UI": { icon: "📦", exp: "Primitives", focus: "Accessible foundations", details: "Configuring headless Radix hooks to ensure complete keyboard navigation access and modal logic." },
  "Framer Motion": { icon: "✨", exp: "Animations", focus: "Premium micro-animations", details: "Creating hardware-accelerated hover states, spring metrics, path drawings, and scroll triggers." },
  "Design Systems": { icon: "🎨", exp: "Scale", focus: "Design token architecture", details: "Setting up design tokens for clean global scaling of colors, margins, and typography components." },
  "Node.js": { icon: "🟢", exp: "REST APIs", focus: "Server endpoints", details: "Building robust REST endpoints, session middleware, and JWT validation." },
  "Express": { icon: "🚂", exp: "APIs", focus: "Routing structures", details: "Writing clean routers, rate limiters, security headers, and error handling middleware." },
  "MongoDB": { icon: "🍃", exp: "NoSQL DB", focus: "Schema queries", details: "Designing flexible schemas, indexing, aggregation pipelines, and configuring production Atlas nodes." },
  "GitHub Actions": { icon: "🛠️", exp: "Deployments", focus: "CI/CD pipelines", details: "Automating package compression, ssh execution scripts, lint tests, and deployment checks." },
  "Docker": { icon: "🐳", exp: "CI/CD Integration", focus: "Containerized environments", details: "Writing multi-stage builds and docker-compose files for local development sandboxes." },
  "Vite": { icon: "⚡", exp: "Build Tool", focus: "Fast compiles", details: "Configuring build pipelines, plugins, dev servers, and fast compile bundles." },
  "GraphQL": { icon: "📊", exp: "APIs", focus: "Queries & Mutations", details: "Designing flexible graph schemas, query structures, and payload optimizations." },
  "Flutter": { icon: "📱", exp: "Cross-platform", focus: "Mobile Apps", details: "Building clean multi-platform mobile applications with custom canvas drawings." },
  "AWS": { icon: "☁️", exp: "Cloud Hosting", focus: "Server deployments", details: "Managing cloud nodes, security groups, database hosting, and CDN setups." }
};

const categoryNameMap = {
  "Frontend Foundations": "Core Web Technologies",
  "React / Next.js Ecosystem": "Frontend Frameworks",
  "UI Libraries & Styling": "Design Systems",
  "Backend / MERN (Learning)": "Backend & APIs",
  "CI/CD & Tooling": "Developer Experience"
};

function SkillCard({ name, rawItem }) {
  const [hovered, setHovered] = useState(false);
  
  // Strip out any suffix colon ratings
  const skillName = name.split(":")[0].trim();
  const staticData = staticSkillsMap[skillName] || {
    icon: "🛠️",
    exp: "Core Skill",
    focus: "Integrated technology",
    details: "Managed and customized within production project environments."
  };

  return (
    <motion.div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      whileHover={{
        y: -4,
        scale: 1.01
      }}
      transition={{ type: "spring", stiffness: 300, damping: 22 }}
      className="relative rounded-2xl glass p-5 border border-border/20 hover:border-primary/20 transition-colors cursor-default overflow-hidden flex flex-col justify-between"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent pointer-events-none" />

      <div>
        <div className="flex justify-between items-center gap-4">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="font-sans text-lg opacity-85 select-none shrink-0">{staticData.icon}</span>
            <span className="font-heading font-semibold text-sm sm:text-base tracking-tight truncate">{skillName}</span>
          </div>
          <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-wider bg-secondary/40 px-2.5 py-1 rounded-md border border-border/10 shrink-0">
            {staticData.exp}
          </span>
        </div>
        
        <div className="mt-3 text-xs sm:text-sm text-foreground/80 font-medium font-sans">
          {staticData.focus}
        </div>

        {/* Expandable Details Block */}
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{
            height: hovered ? "auto" : 0,
            opacity: hovered ? 1 : 0
          }}
          transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          className="overflow-hidden will-change-[height,opacity]"
        >
          <div className="border-t border-border/10 pt-3 mt-3 text-xs text-muted-foreground leading-relaxed">
            {staticData.details}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default function Skills() {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/skills").then((r) => setSkills(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-6 py-10" data-testid="skills-page">
      <Seo title="Tech Stack" description="The tools I rely on to design and build fast, accessible, production-ready products." path="/skills" />
      
      <Reveal>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Tech Stack</div>
        <h1 className="text-4xl sm:text-5xl lg:text-7xl font-heading font-extrabold tracking-tighter mt-3 leading-[1.02] max-w-4xl">
          Core Capabilities.
        </h1>
        <p className="mt-5 text-lg sm:text-xl text-muted-foreground max-w-2xl leading-relaxed">
          The tools I rely on to design and build fast, accessible, production-ready products.
        </p>
      </Reveal>

      <div className="space-y-16 mt-20">
        {loading
          ? Array.from({ length: 4 }).map((_, i) => <SkillCardSkeleton key={i} />)
          : skills.map((g, i) => {
              const categoryTitle = categoryNameMap[g.category] || g.category;
              return (
                <Reveal key={g.id} delay={i * 0.06}>
                  <div className="border-t border-border/20 pt-8" data-testid={`skill-group-${g.id}`}>
                    <div className="flex items-baseline gap-3 mb-6">
                      <span className="font-mono text-xs text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
                      <h2 className="font-heading text-lg sm:text-xl font-medium tracking-tight text-foreground/90">{categoryTitle}</h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {g.items.map((it) => (
                        <SkillCard key={it} name={it} rawItem={it} />
                      ))}
                    </div>
                  </div>
                </Reveal>
              );
            })}
      </div>
    </div>
  );
}
